<template>
  <div class="login-page">
    <form @submit.prevent="doLogin">
      <div class="sub-header">{{ $t('login.pleaseLogIn') }}</div>
      <div class="login-elements-container">
        <LoginFieldEntry
          v-model="username"
          :prompt="$t('login.userName')"
          class="username-entry"
          icon-name="user"
        />
        <LoginFieldEntry
          v-model="password"
          :prompt="$t('login.password')"
          input-type="password"
          class="password-entry"
          icon-name="lock"
        />

        <div class="login-actions">
          <SubmitButton label="login" />
        </div>
        <div class="help-link">
          <router-link to="/help">Need help? </router-link>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import SubmitButton from '../../components/SubmitButton';
import { loginService } from './login.service';
import LoginFieldEntry from './LoginFieldEntry';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import { AppActions, AppGetters } from '@/app/app.keys';
import { STATE_RESET_ACTION_PREFIX } from '@/app/app.keys';
import { NotificationsListingActions } from '@/app/features/notifications/features/notifications-listing/notifications-listing.keys';

const parseJwt = token => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

export default {
  name: 'Login',
  components: {
    LoginFieldEntry,
    SubmitButton
  },
  data() {
    return {
      username: '',
      password: '',
      firstTimeLogin: false,
      time: ''
    };
  },
  computed: {
    firstTimeApplicationAccess() {
      return this.$store.getters[AppGetters.FirstTimeApplicationAccess];
    }
  },
  methods: {
    doLogin() {
      loginService
        .doLogin(this.username, this.password)
        .then(response => {
          if (response.data && response.data.status && response.data.pwResetCode) {
            this.$store.dispatch(AppActions.SetFirstTimeApplicationAccess);
          } else if (
            response.data &&
            !response.data.accessToken &&
            !response.data.refreshToken &&
            response.data.accountVerificationRequired
          ) {
            this.$store.dispatch(AppActions.SetAccountVerificationRequired, {
              required: true,
              username: this.username,
              userFirstname: response.data.userFirstname
            });
          } else {
            localStorage.setItem('tol-token', response.data.accessToken);
            localStorage.setItem('tol-refresh-token', response.data.refreshToken);
            const tokenData = parseJwt(response.data.accessToken);
            const FIVE_MINUTES_BEFORE_EXPIRY =
              (tokenData.exp - Math.floor(Date.now() / 1000) - 300) * 1000;

            this.$store.dispatch(AppActions.SetRefreshTokenModalIsVisible, false);
            //called five minutes before session is about to expire
            //replace existing token with new token which has updated expiry date
            this.$store.dispatch(
              AppActions.InsertTimeOutReferences,
              setTimeout(() => {
                this.$store.dispatch(AppActions.SetRefreshTokenModalIsVisible, true);
              }, FIVE_MINUTES_BEFORE_EXPIRY)
            );
            this.$store.dispatch(AppActions.SetSessionRefreshTime, FIVE_MINUTES_BEFORE_EXPIRY);
            this.resetStore();
            this.$store.dispatch(NotificationsListingActions.RetrieveNotifications);
            this.$router.push(this.$store.state.redirectTo || '/');
            this.resetTimer();
            this.inactivityTime();
          }
        })
        .then(null, () => {
          this.$store.dispatch(TolErrorsActions.SetError, {
            message: this.$t('login.errors.badCredentials')
          });
        });
    },
    inactivityTime() {
      window.onload = this.resetTimer;
      document.onmousemove = this.resetTimer;
      document.onkeydown = this.resetTimer;
    },
    resetTimer() {
      this.$store.dispatch(AppActions.UpdateLastActiveTime);
    },
    resetStore() {
      if (this.$store) {
        for (let key of Object.keys(this.$store.state)) {
          if (key.includes('State')) {
            key = key[0].toUpperCase() + key.slice(1);
            const actionName = STATE_RESET_ACTION_PREFIX + key;
            if (this.$store._actions[actionName]) {
              this.$store._actions[actionName][0]();
            }
          }
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../styles/colors.scss';
@import '../../styles/variables.scss';

.login-page {
  height: 100%;
  width: $login-page-width;
  padding: 50px 25px;
  .sub-header {
    text-transform: uppercase;
    font-weight: bolder;
    font-size: 22pt;
    margin-bottom: 30px;
  }
  .help-link {
    display: flex;
    justify-content: center;
    padding-top: 50px;
  }
  .login-actions {
    display: flex;
    margin-top: 20px;
    justify-content: flex-end;
  }
}
</style>
